/**
 * Admin Dashboard Page
 *
 * Main admin dashboard showing system overview, user statistics,
 * application pipeline, payment tracking, and system health metrics.
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, ClipboardList, DollarSign, Settings, ScrollText, Info, AlertTriangle, Award } from 'lucide-react';
import { PageLayout } from '../../components/layouts/PageLayout';
import { Card, CardHeader, CardBody } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { StatCard } from '../../components/dashboard/StatCard';
import { DashboardGrid } from '../../components/dashboard/DashboardGrid';
import { RecentActivityCard } from '../../components/dashboard/RecentActivityCard';
import { QuickActionsPanel } from '../../components/dashboard/QuickActionsPanel';
import { mockApi } from '../../services/mockApi';
import type { Application } from '../../types/application';

export const AdminDashboardPage: React.FC = () => {
  const { t } = useTranslation(['common', 'admin']);
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState<Application[]>([]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const response = await mockApi.getAllApplications();
      if (response.success && response.data) {
        setApplications(response.data);
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate statistics
  const stats = {
    totalUsers: 45, // Mock - would come from user data
    activeApplications: applications.filter(a =>
      ['SUBMITTED', 'UNDER_REVIEW', 'AUDIT_SCHEDULED', 'AUDIT_IN_PROGRESS'].includes(a.status)
    ).length,
    certificatesIssued: 12, // Mock - for this month
    totalRevenue: 27500, // Mock - MYR
    pendingReviews: applications.filter(a => a.status === 'SUBMITTED').length,
    systemHealth: 'Healthy', // Mock
  };

  // Recent registrations (mock data)
  const recentRegistrations = [
    { id: '1', name: 'Nasi Lemak Express Sdn Bhd', role: 'Company', date: new Date().toISOString() },
    { id: '2', name: 'Dr. Ahmad Halal Consultant', role: 'Consultant', date: new Date(Date.now() - 86400000).toISOString() },
    { id: '3', name: 'Restoran Warisan', role: 'Company', date: new Date(Date.now() - 172800000).toISOString() },
    { id: '4', name: 'Ustaz Mohamed', role: 'Auditor', date: new Date(Date.now() - 259200000).toISOString() },
    { id: '5', name: 'Halal Food Industries', role: 'Company', date: new Date(Date.now() - 345600000).toISOString() },
  ];

  // Recent certificates (mock data)
  const recentCertificates = [
    { id: 'CERT-20250101-001', company: 'Kantin Warisan Sdn Bhd', date: new Date().toISOString() },
    { id: 'CERT-20241230-005', company: 'Restoran Al-Barakah', date: new Date(Date.now() - 86400000).toISOString() },
    { id: 'CERT-20241229-003', company: 'Bakery House', date: new Date(Date.now() - 172800000).toISOString() },
    { id: 'CERT-20241228-002', company: 'Food Processing Co', date: new Date(Date.now() - 259200000).toISOString() },
    { id: 'CERT-20241227-001', company: 'Catering Services Ltd', date: new Date(Date.now() - 345600000).toISOString() },
  ];

  if (loading) {
    return (
      <PageLayout
        pageTitle={t('admin:dashboard.title')}
        breadcrumbs={[
          { label: t('common:navigation.home'), href: '/' },
          { label: t('admin:dashboard.title') },
        ]}
      >
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">{t('common:app.loading')}</p>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      pageTitle={t('admin:dashboard.title')}
      breadcrumbs={[
        { label: t('common:navigation.home'), href: '/' },
        { label: t('admin:dashboard.title') },
      ]}
    >
      {/* Welcome Section */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800">{t('admin:dashboard.welcome')}</h2>
        <p className="text-gray-600 mt-1">{t('admin:dashboard.systemOverview')}</p>
      </div>

      {/* System Overview Cards */}
      <DashboardGrid cols={3} className="mb-6">
        <StatCard
          title={t('admin:dashboard.totalUsers')}
          value={stats.totalUsers}
          icon={<Users className="w-6 h-6" />}
          variant="primary"
          subtitle="Across all roles"
          trend={{
            value: 8,
            direction: 'up',
            label: 'vs last month',
          }}
        />

        <StatCard
          title={t('admin:dashboard.activeApplications')}
          value={stats.activeApplications}
          icon={<ClipboardList className="w-6 h-6" />}
          variant="warning"
          subtitle="In progress"
        />

        <StatCard
          title={t('admin:dashboard.certificatesIssued')}
          value={stats.certificatesIssued}
          icon={<Award className="w-6 h-6" />}
          variant="success"
          subtitle="This month"
        />

        <StatCard
          title={t('admin:dashboard.totalRevenue')}
          value={`MYR ${stats.totalRevenue.toLocaleString()}`}
          icon={<DollarSign className="w-6 h-6" />}
          variant="success"
          subtitle="All time"
        />

        <StatCard
          title={t('admin:dashboard.pendingReviews')}
          value={stats.pendingReviews}
          icon={<AlertTriangle className="w-6 h-6" />}
          variant="warning"
          subtitle="Awaiting action"
        />

        <StatCard
          title={t('admin:dashboard.systemHealth')}
          value={stats.systemHealth}
          icon={<Settings className="w-6 h-6" />}
          variant="success"
          subtitle="All systems operational"
        />
      </DashboardGrid>

      {/* Application Pipeline */}
      <Card className="mb-6">
        <CardHeader>
          <h3 className="text-lg font-semibold">{t('admin:dashboard.applicationPipeline')}</h3>
        </CardHeader>
        <CardBody>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">Submitted</p>
              <p className="text-2xl font-bold text-gray-800">
                {applications.filter(a => a.status === 'SUBMITTED').length}
              </p>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-gray-600">Under Review</p>
              <p className="text-2xl font-bold text-blue-600">
                {applications.filter(a => a.status === 'UNDER_REVIEW').length}
              </p>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <p className="text-sm text-gray-600">Audit Scheduled</p>
              <p className="text-2xl font-bold text-purple-600">
                {applications.filter(a => a.status === 'AUDIT_SCHEDULED').length}
              </p>
            </div>
            <div className="text-center p-4 bg-orange-50 rounded-lg">
              <p className="text-sm text-gray-600">In Progress</p>
              <p className="text-2xl font-bold text-orange-600">
                {applications.filter(a => a.status === 'AUDIT_IN_PROGRESS').length}
              </p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-gray-600">Approved</p>
              <p className="text-2xl font-bold text-green-600">
                {applications.filter(a => a.status === 'APPROVED').length}
              </p>
            </div>
          </div>
        </CardBody>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Recent User Registrations */}
        <RecentActivityCard
          title={t('admin:dashboard.recentRegistrations')}
          items={recentRegistrations.map((user) => ({
            id: user.id,
            icon: <Users className="w-4 h-4" />,
            title: user.name,
            description: user.role,
            timestamp: new Date(user.date),
            variant: 'primary',
            onClick: () => window.location.href = '/admin/users',
          }))}
          maxItems={5}
          showViewAll
          onViewAllClick={() => window.location.href = '/admin/users'}
        />

        {/* Recent Certificate Issuances */}
        <RecentActivityCard
          title={t('admin:dashboard.recentCertificates')}
          items={recentCertificates.map((cert) => ({
            id: cert.id,
            icon: <Award className="w-4 h-4" />,
            title: cert.company,
            description: cert.id,
            timestamp: new Date(cert.date),
            variant: 'success',
            onClick: () => window.location.href = '/admin/certificates',
          }))}
          maxItems={5}
          showViewAll
          onViewAllClick={() => window.location.href = '/admin/certificates'}
        />
      </div>

      {/* Payment Summary & Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Payment Summary */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">{t('admin:dashboard.paymentSummary')}</h3>
              <Button variant="outline" size="sm" onClick={() => window.location.href = '/admin/payments'}>
                {t('admin:dashboard.viewPaymentRecords')}
              </Button>
            </div>
          </CardHeader>
          <CardBody>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <p className="text-sm text-gray-600">Completed</p>
                <p className="text-2xl font-bold text-green-600">35</p>
              </div>
              <div className="text-center p-4 bg-yellow-50 rounded-lg">
                <p className="text-sm text-gray-600">Pending</p>
                <p className="text-2xl font-bold text-yellow-600">8</p>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <p className="text-sm text-gray-600">Failed</p>
                <p className="text-2xl font-bold text-red-600">2</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">Refunded</p>
                <p className="text-2xl font-bold text-gray-600">1</p>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Quick Actions */}
        <QuickActionsPanel
          title={t('admin:dashboard.quickActions')}
          layout="vertical"
          actions={[
            {
              id: 'users',
              label: 'Manage Users',
              icon: <Users className="w-4 h-4" />,
              onClick: () => window.location.href = '/admin/users',
              variant: 'outline',
            },
            {
              id: 'issue-cert',
              label: 'Issue Certificate',
              icon: <ScrollText className="w-4 h-4" />,
              onClick: () => window.location.href = '/admin/certificates/issue',
              variant: 'outline',
            },
            {
              id: 'applications',
              label: 'View Applications',
              icon: <ClipboardList className="w-4 h-4" />,
              onClick: () => window.location.href = '/admin/applications',
              variant: 'outline',
            },
            {
              id: 'payments',
              label: 'Payment Records',
              icon: <DollarSign className="w-4 h-4" />,
              onClick: () => window.location.href = '/admin/payments',
              variant: 'outline',
            },
            {
              id: 'settings',
              label: 'System Settings',
              icon: <Settings className="w-4 h-4" />,
              onClick: () => window.location.href = '/admin/settings',
              variant: 'outline',
            },
          ]}
        />
      </div>

      {/* System Alerts (Optional) */}
      <Card className="mt-6">
        <CardHeader>
          <h3 className="text-lg font-semibold">{t('admin:dashboard.systemAlerts')}</h3>
        </CardHeader>
        <CardBody>
          <div className="space-y-2">
            <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <Info className="w-6 h-6 text-info-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-blue-900">System Update Available</p>
                <p className="text-sm text-blue-700">A new version is available. Schedule maintenance to update.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <AlertTriangle className="w-6 h-6 text-warning-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-yellow-900">Certificates Expiring Soon</p>
                <p className="text-sm text-yellow-700">3 certificates will expire within the next 30 days.</p>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>
    </PageLayout>
  );
};

export default AdminDashboardPage;
